import { Controller, useForm } from "react-hook-form";
import { H3 } from "../../components/headline/Headline";
import { InputField } from "../../components/input/InputFields";
import { PATTERN_EMAIL, PATTERN_NAME, PATTERN_VOUCHER_TEXT } from "../booking/identification/types";
import { Textarea } from "../../components/input/Textarea";
import DatePicker from "react-datepicker";
import de from "date-fns/locale/de";
import { Buttons } from "../../components/button/Button";
import { ContactFormData, Intention } from "./ContactFormHandler";
import { setHours, setMinutes } from "date-fns";

export interface ContactFormProps {
    headline: string,
    intention: Intention,
    onSubmit: (data: ContactFormData) => void;
}

export function getAttendeesValidation(intention: Intention) {
    return (value?: number) => {
        if (intention !== 'LARGE_GROUP_PUBLIC') {
            return true;
        }

        if (value === undefined || value === null || Number.isNaN(value)) {
            return 'Bitte gib hier eine gültige Teilnehmeranzahl an';
        }

        return value >= 10 || 'Bitte gib mindestens 10 Teilnehmende an';
    };
}

export default function ContactForm(props: ContactFormProps) {

    const { register, handleSubmit, control, formState: { errors }, setValue, getValues } = useForm<ContactFormData>();

    return <>
        <H3 text={props.headline} />
        <form onSubmit={handleSubmit(props.onSubmit)}>
            <div className="half">
                {/* Vorname */}
                <InputField
                    label='Vorname'
                    register={register('firstName', { required: true, pattern: PATTERN_NAME })}
                    placeholder='Vorname'
                    name='firstName'
                    hasError={Boolean(errors?.firstName)}
                    errorMessage='Bitte gib Deinen Vornamen an'
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        setValue("firstName", e.currentTarget.value);
                    }} />

                {/* Nachname */}
                <InputField
                    label='Nachname'
                    register={register('lastName', { required: true, pattern: PATTERN_NAME })}
                    placeholder='Nachname'
                    name='lastName'
                    hasError={Boolean(errors?.lastName)}
                    errorMessage='Bitte gib Deinen Nachnamen an'
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        setValue("lastName", e.currentTarget.value);
                    }} />
            </div>

            {/* E-Mail */}
            <InputField
                label='E-Mail'
                register={register('email', {
                    required: true,
                    pattern: {
                        value: PATTERN_EMAIL,
                        message: 'Bitte gib eine valide E-Mail-Adresse an'
                    },
                })}
                placeholder='E-Mail'
                name='email'
                hasError={Boolean(errors?.email)}
                errorMessage={errors?.email?.message}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                    setValue("email", e.currentTarget.value);
                }} />

            <InputField
                label="Anzahl der Teilnehmenden"
                register={register('attendees', {
                    valueAsNumber: true,
                    validate: getAttendeesValidation(props.intention),
                })}
                type={'number'}
                name='attendees'
                placeholder='Voraussichtliche Anzahl der Teilnehmenden'
                hasError={Boolean(errors?.attendees)}
                errorMessage={errors?.attendees?.message || 'Bitte gib hier eine gültige Teilnehmeranzahl an'}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                    const nextValue = e.currentTarget.value === '' ? undefined : parseInt(e.currentTarget.value, 10);
                    setValue('attendees', nextValue as number, { shouldValidate: true });
                }}
            />

            <div className="half">
                <div className="input-wrapper">
                    <label className="label" htmlFor="start">Von</label>
                    <Controller
                        control={control}
                        name='start'
                        rules={{
                            required: "Bitte gib einen Zeitpunkt an",
                            validate: {
                                isBeforeEnd: (start) => {
                                    const { end } = getValues();
                                    return (end === undefined || start.getTime() < end.getTime()) || 'Der Zeitpunkt liegt nach dem Ende'
                                }
                            }
                        }}
                        render={({ field }) => (
                            <DatePicker
                                selected={field.value}
                                onChange={(date: Date) => field.onChange(date)}
                                showTimeSelect
                                timeFormat="p"
                                timeCaption="Uhrzeit"
                                timeIntervals={30}
                                dateFormat="Pp"
                                locale={de}
                                className={errors?.start ? 'input-error' : 'input-valid'}
                                minDate={new Date()}
                                minTime={setHours(setMinutes(new Date(), 0), 6)}
                                maxTime={setHours(setMinutes(new Date(), 0), 22)}
                            />
                        )}
                    />
                    {errors?.start &&
                        <span className='input-error-text'>
                            {errors?.start?.message}
                        </span>
                    }
                </div>


                <div className="input-wrapper">
                    <label className="label" htmlFor="end">Bis</label>
                    <Controller
                        control={control}
                        name='end'
                        rules={{
                            required: "Bitte gib einen Zeitpunkt an",
                            validate: {
                                isAfterEnd: (end) => {
                                    const { start } = getValues();
                                    return (start === undefined || end.getTime() > start.getTime()) || 'Der Zeitpunkt liegt vor dem Start'
                                }
                            }
                        }}
                        render={({ field }) => (
                            <DatePicker
                                selected={field.value}
                                onChange={(date: Date) => field.onChange(date)}
                                showTimeSelect
                                timeFormat="p"
                                timeCaption="Uhrzeit"
                                timeIntervals={30}
                                dateFormat="Pp"
                                locale={de}
                                className={errors?.end ? 'input-error' : 'input-valid'}
                                minDate={new Date()}
                                minTime={setHours(setMinutes(new Date(), 0), 6)}
                                maxTime={setHours(setMinutes(new Date(), 0), 22)}
                            />
                        )}
                    />
                    {errors?.end &&
                        <span className='input-error-text'>
                            {errors?.end?.message}
                        </span>
                    }
                </div>
            </div>

            <Textarea
                register={register('text', { maxLength: 250, pattern: PATTERN_VOUCHER_TEXT })}
                name="text"
                hasError={Boolean(errors?.text)}
                errorMessage={'Bitte gib nur gültige Zeichen ein. Erlaubt sind Groß- und Kleinbuchstaben, sowie die Sonderzeichen ,.\'-_#()!?'}
                label="Sonstiges"
                maxLength={250}
                maxRows={5}
            />

            <Buttons submitButtonText={'Absenden'} />
        </form>

    </>
}